import { $convertFromMarkdownString } from "@lexical/markdown"
import { $getRoot, $isElementNode, ElementNode } from "lexical"
import { EditorInstance } from "../LexicalEditor"
import { EDITOR_CONFIG, LexicalConfigType } from "../config/LexicalConfig"
import { getMarkdownTransformers } from "../plugins/markdown/MarkdownPlugin"
import { createLexicalTableFromData } from "./createLexicalTable"

export interface MarkdownTableData {
  headers: string[]
  rows: string[][]
}

// Check if markdown contains tables that are NOT inside code blocks
export function hasMarkdownTables(markdown: string): boolean {
  const tables = getTablesOutsideCodeBlocks(markdown)
  return tables.length > 0
}

// Extract table data only from tables that are NOT inside code blocks
export function extractTableData(markdown: string): MarkdownTableData[] {
  const tables = getTablesOutsideCodeBlocks(markdown)
  return tables
    .map((tableText) => parseTableText(tableText))
    .filter(Boolean) as MarkdownTableData[]
}

export function convertMarkdownTablesToLexical(
  markdown: string,
  editor: EditorInstance,
  configType: LexicalConfigType
) {
  const { blocks, inlineTools } = EDITOR_CONFIG[configType]

  if (hasMarkdownTables(markdown)) {
    // Get tables that are outside code blocks
    const tablesData = extractTableData(markdown)
    let processedMarkdown = markdown

    // Replace tables outside code blocks with placeholders
    const tablesOutsideCodeBlocks = getTablesOutsideCodeBlocks(markdown)
    for (let i = tablesOutsideCodeBlocks.length - 1; i >= 0; i--) {
      const tableText = tablesOutsideCodeBlocks[i]
      const tablePlaceholder = `\n\nTABLE_PLACEHOLDER_${i}\n\n`
      // Find and replace this specific table text
      const tableIndex = processedMarkdown.lastIndexOf(tableText)
      if (tableIndex !== -1) {
        processedMarkdown =
          processedMarkdown.slice(0, tableIndex) +
          tablePlaceholder +
          processedMarkdown.slice(tableIndex + tableText.length)
      }
    }

    $convertFromMarkdownString(
      processedMarkdown,
      getMarkdownTransformers({ blocks, inlineTools })
    )

    editor.update(() => {
      const root = $getRoot()

      function findAndReplaceTablePlaceholders(node: ElementNode) {
        const children = node.getChildren()

        for (const child of children) {
          const text = child.getTextContent().trim()

          // Check if this is a table placeholder
          const placeholderMatch = text.match(/^TABLE_PLACEHOLDER_(\d+)$/)
          if (placeholderMatch) {
            const index = parseInt(placeholderMatch[1], 10)
            const tableData = tablesData[index]

            if (tableData) {
              const tableNode = createLexicalTableFromData(
                tableData.headers,
                tableData.rows
              )
              if (tableNode) {
                child.replace(tableNode)
                continue
              }
            }
          }

          // Recursively search in element nodes
          if ($isElementNode(child)) {
            findAndReplaceTablePlaceholders(child)
          }
        }
      }

      findAndReplaceTablePlaceholders(root)
    })
  } else {
    $convertFromMarkdownString(markdown, getMarkdownTransformers({ blocks, inlineTools }))
  }
}

// Parse a single table text into table data
export function parseTableText(tableText: string): MarkdownTableData | null {
  const lines = tableText
    .trim()
    .split("\n")
    .filter((line) => line.trim())

  if (lines.length < 2) return null // Header + separator is minimum

  const headerLine = lines[0]
  const dataLines = lines.length > 2 ? lines.slice(2) : []

  // Parse header row
  const headers = headerLine
    .split("|")
    .map((cell: string) => cell.trim())
    .filter((cell: string) => cell !== "")

  // Parse data rows
  const rows = dataLines.map((row: string) => {
    const cells = row
      .split("|")
      .map((cell: string) => cell.trim())
      .filter((cell: string) => cell !== "")
    // Ensure row has same number of columns as header
    while (cells.length < headers.length) {
      cells.push("")
    }
    return cells.slice(0, headers.length)
  })

  if (headers.length === 0) return null

  return { headers, rows }
}

// Get all markdown tables that are outside of code blocks
export function getTablesOutsideCodeBlocks(markdown: string): string[] {
  const tableRegex = /(\|[^\n]+\|\n\|[-\s:|]+\|\n(?:\|[^\n]+\|\n?)*)/gm
  const codeBlockRegex = /```[\s\S]*?```/gm

  // Find all code blocks and their positions
  const codeBlocks: Array<{ start: number; end: number }> = []
  let codeBlockMatch
  while ((codeBlockMatch = codeBlockRegex.exec(markdown)) !== null) {
    codeBlocks.push({
      start: codeBlockMatch.index,
      end: codeBlockMatch.index + codeBlockMatch[0].length,
    })
  }

  // Find all tables and check if they're inside code blocks
  const tablesOutsideCodeBlocks: string[] = []
  let tableMatch
  while ((tableMatch = tableRegex.exec(markdown)) !== null) {
    const tableStart = tableMatch.index
    const tableEnd = tableMatch.index + tableMatch[0].length

    // Check if this table is inside any code block
    const isInsideCodeBlock = codeBlocks.some(
      (block) => tableStart >= block.start && tableEnd <= block.end
    )

    if (!isInsideCodeBlock) {
      tablesOutsideCodeBlocks.push(tableMatch[1])
    }
  }

  return tablesOutsideCodeBlocks
}
